// Desktop dashboard — the pre-W36.0 page, moved here unchanged.
//
// This component is the **verbatim** pre-W36.0 `/dashboard`, relocated because
// AGENTS.md §3 #10 caps a frontend route at 15 KB and the file grew past it
// once the W36.0 mobile branch was added alongside. Nothing about its behaviour
// changed: same queries, same copy, same testids.
//
// Spec §2.1 Q4 locks this: 「本輪僅移動端; 桌面端維持現狀」. Keeping the two
// branches in separate files makes that boundary structural — a future desktop
// redesign cannot accidentally reflow into the mobile layout, and the mobile
// branch's imports cannot reach back into this one.
//
// It deliberately does NOT use `useDashboard()`: the desktop first paint still
// makes its own two requests (`readPair` + `listDiaries`). Changing that is
// desktop scope, not this sprint's.

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookHeart, Calendar, Settings2 } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InfoBanner } from "@/components/primitives/InfoBanner";
import { LoadingState } from "@/components/primitives/LoadingState";
import { EmptyState } from "@/components/primitives/EmptyState";
import { PullToRefreshBar } from "@/components/patterns/PullToRefreshBar";
import { PageContainer, RowGap } from "@/components/layout";
import { useAuth } from "@/lib/auth/AuthProvider";
import { readPair } from "@/lib/api/pair";
import { listDiaries, type DiaryOut } from "@/lib/api/diary";
import { queryKeys } from "@/lib/queries/keys";
import { DIARY_I18N } from "@/lib/i18n/diary";
import { previewFromHtml } from "@/lib/utils/text";
import { formatAbsoluteZh, formatRelative } from "@/lib/utils/date";
import { cn } from "@/lib/utils/cn";

export function DesktopDashboard() {
  const { user, pairId, status } = useAuth();
  const today = useMemo(() => new Date(), []);

  const pairQuery = useQuery({
    queryKey: queryKeys.pairs.detail(pairId ?? "active"),
    queryFn: () => readPair(pairId!),
    enabled: Boolean(pairId),
    retry: false,
  });

  // W40.4. This asks for `include_private=true` on purpose. The flag used to be
  // inverted in the service layer, and the `false` branch it took then was
  // literally this expression — "everything public, plus my own private" —
  // which is what the fix's `true` branch now says. Passing `true` therefore
  // keeps 「最近的日記」 byte-identical across the fix; passing `false` would
  // have quietly started hiding the viewer's own private entries here, which is
  // not what this round set out to change.
  const diaryQuery = useQuery({
    queryKey: queryKeys.diary.list(null, true),
    queryFn: () => listDiaries(null, 3, true),
    enabled: Boolean(pairId),
    staleTime: 30_000,
  });

  const isLoading = status === "loading" || pairQuery.isLoading;

  return (
    <div data-testid="dashboard-desktop-page" className="flex flex-col">
      <PageHero
        title={user ? `你好, ${user.display_name}` : "今天"}
        subtitle={formatAbsoluteZh(today)}
      />
      <PageContainer vertical="default">
        <PullToRefreshBar
          onRefresh={async () => {
            await Promise.all([pairQuery.refetch(), diaryQuery.refetch()]);
          }}
          data-testid="dashboard-pull-to-refresh"
        >
          {user && !user.email_verified_at && user.email ? (
            <InfoBanner tone="partner">請驗證你的電子郵件，未驗證前部分功能將受限制。</InfoBanner>
          ) : null}

          {pairId ? (
            <Card>
              <div className="p-5 flex flex-col gap-5" data-testid="dashboard-pair">
                {pairQuery.isLoading ? (
                  <LoadingState count={1} />
                ) : pairQuery.isError || !pairQuery.data ? (
                  <p className="text-body-sm text-partner-strong">找不到 Pair 資料, 請重新整理。</p>
                ) : (
                  <>
                    <header className="flex items-center justify-between">
                      <div>
                        <p className="text-meta text-ink-3">我們的空間</p>
                        <h2 className="font-display text-title-2 text-ink-1">
                          {pairQuery.data.pair.name}
                        </h2>
                      </div>
                      <Link
                        to="/settings/pair"
                        aria-label="空間設定"
                        data-testid="dashboard-pair-settings"
                        // W46.0 — `inline-flex h-11 w-11 items-center
                        // justify-center -m-2`: this was a bare 16×16 icon link,
                        // the smallest target found anywhere in the app by the
                        // desktop sweep. The negative margin keeps the glyph on
                        // its original spot inside the header row, so only the
                        // hit area grows.
                        className="inline-flex h-11 w-11 items-center justify-center -m-2"
                      >
                        <Settings2 className="w-4 h-4 text-ink-3 hover:text-ink-1" />
                      </Link>
                    </header>
                    <p className="text-body-sm text-ink-2">
                      交往第 <DaysCounter startDate={pairQuery.data.pair.relationship_start} /> 天
                    </p>
                  </>
                )}
              </div>
            </Card>
          ) : (
            <Card>
              <div className="p-5 flex flex-col gap-4" data-testid="dashboard-no-pair">
                <h2 className="font-display text-title-3 text-ink-1">
                  還沒有配對, 來邀請你的伴侶嗎?
                </h2>
                <p className="text-body-sm text-ink-2">
                  配對完成後, 你們的共享空間才會開始累積天數。
                </p>
                <div className="flex gap-2">
                  <Link to="/pair" data-testid="link-pair">
                    <Button variant="primary">去配對</Button>
                  </Link>
                </div>
              </div>
            </Card>
          )}

          {pairId ? (
            <div className="grid grid-cols-2 gap-s-4" data-testid="dashboard-actions">
              <Link to="/diaries/new" data-testid="quick-new-diary">
                <Button
                  variant="primary"
                  className="w-full"
                  leadingIcon={<BookHeart className="w-3.5 h-3.5" />}
                >
                  寫日記
                </Button>
              </Link>
              <Link to="/calendar" data-testid="quick-calendar">
                <Button
                  variant="secondary"
                  className="w-full"
                  leadingIcon={<Calendar className="w-3.5 h-3.5" />}
                >
                  行事曆
                </Button>
              </Link>
            </div>
          ) : null}

          {pairId ? (
            <RowGap as="section" density="default" className="flex flex-col" data-testid="dashboard-recent">
              <header className="flex items-center justify-between px-1">
                <h3 className="font-display text-title-3 text-ink-1">最近的日記</h3>
                <Link
                  to="/diaries"
                  // W46.0 — `inline-flex min-h-11 items-center -my-2 px-1
                  // -mx-1`: measured 43.5 × 22, under the floor in height.
                  // A bare text link's box is its type, which is the same
                  // mistake the wordmark and the back links made.
                  className="inline-flex min-h-11 items-center -my-2 px-1 -mx-1 text-body-sm text-you-strong"
                  data-testid="see-all-diaries"
                >
                  全部 <ArrowRight className="w-3 h-3 inline-block align-middle" />
                </Link>
              </header>
              {diaryQuery.isLoading ? (
                <LoadingState count={2} />
              ) : diaryQuery.isError ? (
                <InfoBanner tone="partner">日記載入失敗, 請稍後再試。</InfoBanner>
              ) : (diaryQuery.data?.items ?? []).length === 0 ? (
                <EmptyState
                  title="還沒有日記"
                  description="寫下今天的第一篇"
                  cta={
                    <Link to="/diaries/new">
                      <Button variant="primary">新增第一篇</Button>
                    </Link>
                  }
                />
              ) : (
                (diaryQuery.data?.items ?? [])
                  // Defense in depth: the backend already excludes a
                  // partner's private diaries from non-owner views; re-filter
                  // here against the hearttime v1 bug where it did not.
                  .filter((d: DiaryOut) => !(d.is_private && d.author_id !== user?.id))
                  .map((d: DiaryOut) => (
                    <Link
                      key={d.id}
                      to={`/diaries/${d.id}`}
                      data-testid={`dashboard-diary-${d.id}`}
                    >
                      <Card>
                        <div className="p-4 flex flex-col gap-3">
                          <div className="flex items-center justify-between text-meta text-ink-3">
                            <span>{d.date}</span>
                            {d.mood ? (
                              <span aria-hidden>{DIARY_I18N.mood_emoji(d.mood)}</span>
                            ) : null}
                            {d.is_private ? <span className="text-you-strong">私人</span> : null}
                          </div>
                          {d.title ? (
                            <h4 className={cn("font-display text-title-3 text-ink-1")}>
                              {d.title}
                            </h4>
                          ) : null}
                          {/* `diary.content` is Tiptap HTML, not plain text.
                              Rendering it raw made the user read literal
                              "<p>…</p>" in the card — the W38 browser audit
                              caught this on /dashboard while /diaries, which
                              already used previewFromHtml, looked correct.
                              Same helper, same 140-char cap as Diaries.tsx. */}
                          <p className="text-body text-ink-2 line-clamp-2">
                            {previewFromHtml(d.content, 140)}
                          </p>
                          <p className="text-meta text-ink-3">
                            {formatRelative(new Date(d.created_at))}
                          </p>
                        </div>
                      </Card>
                    </Link>
                  ))
              )}
            </RowGap>
          ) : null}

          {isLoading ? <LoadingState count={1} /> : null}
        </PullToRefreshBar>
      </PageContainer>
    </div>
  );
}

function DaysCounter({ startDate }: { startDate: string }) {
  const days = useMemo(() => {
    const start = new Date(`${startDate}T00:00:00`);
    if (Number.isNaN(start.getTime())) return 0;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diff = now.getTime() - start.getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  }, [startDate]);
  return <span className="text-you-strong font-medium">{days}</span>;
}
